import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CopyOutlined, KeyOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { formatDateTime } from '@/utils/format';
import {
  externalApiKeyService,
  type CreatedExternalApiKey,
  type ExternalApiKey,
} from '@/services/externalApiKey.service';

const { Title, Text, Paragraph } = Typography;

function keyStatus(k: ExternalApiKey): { label: string; color: string } {
  if (!k.is_active || k.revoked_at) return { label: 'Revoked', color: 'red' };
  if (k.expires_at && new Date(k.expires_at).getTime() <= Date.now()) return { label: 'Expired', color: 'orange' };
  return { label: 'Active', color: 'green' };
}

export const ApiKeysPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(user?.is_super_admin || user?.permissions?.includes('company.api_key.manage'));
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedExternalApiKey | null>(null);
  const [form] = Form.useForm<{ name: string; expires_at?: Dayjs | null }>();

  const { data: keys = [], isLoading } = useQuery({
    queryKey: ['external-api-keys', user?.company_id],
    queryFn: () => externalApiKeyService.list(),
  });

  const createMutation = useMutation({
    mutationFn: (values: { name: string; expires_at?: Dayjs | null }) =>
      externalApiKeyService.create({
        name: values.name,
        expires_at: values.expires_at ? values.expires_at.endOf('day').toISOString() : null,
      }),
    onSuccess: (key) => {
      setCreateOpen(false);
      form.resetFields();
      setCreated(key);
      void queryClient.invalidateQueries({ queryKey: ['external-api-keys'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to create API key'),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => externalApiKeyService.revoke(id),
    onSuccess: () => {
      message.success('API key revoked');
      void queryClient.invalidateQueries({ queryKey: ['external-api-keys'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to revoke API key'),
  });

  const confirmRevoke = (k: ExternalApiKey) =>
    Modal.confirm({
      title: `Revoke "${k.name}"?`,
      content: 'Applications using this key will immediately get 401 Unauthorized. This cannot be undone.',
      okText: 'Revoke',
      okButtonProps: { danger: true },
      onOk: () => revokeMutation.mutateAsync(k.id),
    });

  const copyKey = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success('API key copied');
    } catch {
      message.warning('Copy failed — select the key and copy it manually');
    }
  };

  const columns: ColumnsType<ExternalApiKey> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Key',
      dataIndex: 'key_prefix',
      key: 'key_prefix',
      render: (p: string) => <Text code>{p}_••••••••</Text>,
    },
    {
      title: 'Scopes',
      dataIndex: 'scopes',
      key: 'scopes',
      render: (s: string[]) => (
        <Space wrap size={[4, 4]}>
          {(s ?? []).map((x) => (
            <Tag key={x}>{x}</Tag>
          ))}
        </Space>
      ),
    },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (v) => formatDateTime(v) },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (v) => (v ? formatDateTime(v) : <Text type="secondary">Never</Text>),
    },
    {
      title: 'Last used',
      key: 'last_used_at',
      render: (_, k) =>
        k.last_used_at ? (
          <Space direction="vertical" size={0}>
            <span>{formatDateTime(k.last_used_at)}</span>
            {k.last_used_ip ? <Text type="secondary" style={{ fontSize: 11 }}>{k.last_used_ip}</Text> : null}
          </Space>
        ) : (
          <Text type="secondary">Never</Text>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, k) => {
        const s = keyStatus(k);
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, k) =>
        canManage && keyStatus(k).label === 'Active' ? (
          <Button danger size="small" icon={<StopOutlined />} onClick={() => confirmRevoke(k)}>
            Revoke
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <KeyOutlined /> API Keys
          </Title>
          <Text type="secondary">
            Read-only keys that let other applications access this company&apos;s customers, projects and work orders.
          </Text>
        </div>
        {canManage ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New API key
          </Button>
        ) : null}
      </Space>

      <Card>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Each key only sees data for the company it was created in."
          description={
            <>
              Send the key in the <Text code>X-API-Key</Text> header to <Text code>/api/v1/external/directory</Text>.
              See the integration guide (docs/integrations/external-api.md) for parameters and examples.
            </>
          }
        />
        <Table rowKey="id" loading={isLoading} columns={columns} dataSource={keys} pagination={{ pageSize: 20 }} />
      </Card>

      <Modal
        title="New API key"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        okText="Create key"
        confirmLoading={createMutation.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(v) => createMutation.mutate(v)}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Enter a name that identifies the application' }]}
            extra="e.g. the name of the application that will use this key"
          >
            <Input maxLength={120} placeholder="Customer portal" />
          </Form.Item>
          <Form.Item name="expires_at" label="Expires on (optional)">
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isBefore(new Date(), 'day')} />
          </Form.Item>
          <Text type="secondary">Scope: directory.read (customers, projects, work orders — read-only)</Text>
        </Form>
      </Modal>

      <Modal
        title="Copy your API key now"
        open={!!created}
        onCancel={() => setCreated(null)}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={() => created && copyKey(created.plain_key)}>
            Copy
          </Button>,
          <Button key="done" type="primary" onClick={() => setCreated(null)}>
            I have stored it safely
          </Button>,
        ]}
        maskClosable={false}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This key will not be shown again. Store it in the other application's server-side secrets."
        />
        <Paragraph copyable={{ text: created?.plain_key ?? '' }} code style={{ wordBreak: 'break-all' }}>
          {created?.plain_key}
        </Paragraph>
      </Modal>
    </div>
  );
};

export default ApiKeysPage;
